import { useEffect, useRef, useState } from 'react';
import { Box, IconButton, LinearProgress, Typography } from '@mui/material';
import type { Theme } from '@mui/material';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import { toast } from 'react-toastify';
import { SortableGrid } from '../dnd/SortableGrid';
import { isImage } from '../../utils/image';
import { galleryThumbnailUrl } from '../../utils/cloudinary';
import { GalleryImage } from '../../types/gallery';
import {
  GALLERY_MAX_FILE_BYTES,
  GALLERY_MAX_IMAGES,
} from '../../constants/gallery';
import { UseGalleryUploadsResult } from './useGalleryUploads';

const resetButton = {
  p: 0,
  m: 0,
  border: 'none',
  background: 'none',
  font: 'inherit',
  color: 'inherit',
  cursor: 'pointer',
  textAlign: 'left' as const,
};

/** Shared chrome for the handle and remove button sitting on a tile. */
const tileChromeSx = {
  position: 'absolute',
  top: '6px',
  width: 26,
  height: 26,
  padding: 0,
  borderRadius: '5px',
  border: 'none',
  bgcolor: 'rgba(255, 255, 255, 0.92)',
  color: (theme: Theme) => theme.palette.text.primary,
  '&:hover': { bgcolor: (theme: Theme) => theme.palette.common.white },
} as const;

interface GallerySectionProps {
  images: GalleryImage[];
  onChange: (images: GalleryImage[]) => void;
  uploads: UseGalleryUploadsResult;
  disabled?: boolean;
  dirty?: boolean;
  leadCount: number;
  /** Drives the column count, so it follows the container, not the owner. */
  tileMinWidth: number;
  helpText: string;
  emptyDetail: string;
}

function TileBadge({ index, leadCount }: { index: number; leadCount: number }) {
  const isLead = index < leadCount;
  const isCover = index === 0;
  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: '6px',
        left: '6px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Fixed square for numbers so they are true circles at any digit
        // count; only the label grows.
        width: isCover ? 'auto' : 22,
        height: 22,
        padding: isCover ? '0 9px' : 0,
        borderRadius: '50px',
        fontSize: 'overline.fontSize',
        fontWeight: 700,
        lineHeight: 1,
        bgcolor: theme =>
          isLead ? theme.palette.primary.main : 'rgba(255, 255, 255, 0.92)',
        color: theme =>
          isLead ? theme.palette.common.white : theme.palette.text.primary,
      }}>
      {isCover ? 'Cover' : index + 1}
    </Box>
  );
}

function OverlayButton({
  children,
  onClick,
  danger,
}: {
  children: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        ...resetButton,
        // Share the row evenly and compress together, so the pair stays side
        // by side down to the narrowest tile.
        flex: '1 1 0',
        minWidth: 0,
        padding: '6px 2px',
        // resetButton left-aligns; equal-width buttons need the label centred.
        textAlign: 'center',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        borderRadius: '5px',
        border: '1px solid',
        borderColor: theme =>
          danger ? theme.palette.error.main : theme.palette.common.white,
        bgcolor: theme => (danger ? theme.palette.error.main : 'transparent'),
        color: theme => theme.palette.common.white,
        fontSize: 'overline.fontSize',
        lineHeight: 1,
        fontWeight: 700,
        '&:hover': {
          bgcolor: theme =>
            danger ? theme.palette.error.dark : 'rgba(255, 255, 255, 0.16)',
          borderColor: theme =>
            danger ? theme.palette.error.dark : theme.palette.common.white,
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: theme => theme.palette.common.white,
          outlineOffset: '2px',
        },
      }}>
      {children}
    </Box>
  );
}

function UploadTile({
  row,
  onRetry,
  onDismiss,
}: {
  row: UseGalleryUploadsResult['rows'][number];
  onRetry: () => void;
  onDismiss: () => void;
}) {
  return (
    <Box
      sx={{
        position: 'relative',
        aspectRatio: '1',
        borderRadius: 1.5,
        overflow: 'hidden',
        bgcolor: 'action.hover',
      }}>
      <Box
        component="img"
        src={row.previewUrl}
        alt=""
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          opacity: 0.45,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          gap: 0.5,
          p: 1,
          background:
            'linear-gradient(to top, rgba(0,0,0,0.72), rgba(0,0,0,0.15))',
        }}>
        <Typography
          variant="caption"
          noWrap
          title={row.errorMessage}
          sx={{ color: 'common.white', fontWeight: 600 }}>
          {row.status === 'error' ? 'Upload failed' : row.fileName}
        </Typography>
        {row.status === 'uploading' ? (
          <LinearProgress
            variant="determinate"
            value={row.progress}
            sx={{ borderRadius: 1, bgcolor: 'rgba(255,255,255,0.3)' }}
          />
        ) : (
          <Box sx={{ display: 'flex', gap: '4px' }}>
            <OverlayButton onClick={onRetry}>Retry</OverlayButton>
            <OverlayButton danger onClick={onDismiss}>
              Dismiss
            </OverlayButton>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function GallerySection({
  images,
  onChange,
  uploads,
  disabled,
  dirty,
  leadCount,
  tileMinWidth,
  helpText,
  emptyDetail,
}: GallerySectionProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [pendingRemove, setPendingRemove] = useState<string | null>(null);
  const uploadingCount = uploads.rows.filter(
    row => row.status === 'uploading',
  ).length;
  const total = images.length + uploadingCount;
  const isAtCap = total >= GALLERY_MAX_IMAGES;
  const isEmpty = !images.length && !uploads.rows.length;

  const imagesRef = useRef(images);
  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  const onFilesPicked = async (fileList: FileList | null) => {
    const files = fileList ? Array.from(fileList) : [];

    const input = inputRef.current;
    if (input) input.value = '';
    if (!files.length || disabled) return;

    const supported = files.filter(isImage);
    if (supported.length !== files.length) {
      toast.error('Choose JPG, PNG, WEBP or HEIC images.');
    }

    // Caught here rather than after a long upload Cloudinary would reject.
    const accepted = supported.filter(
      file => file.size <= GALLERY_MAX_FILE_BYTES,
    );
    if (accepted.length !== supported.length) {
      toast.error(
        `Each photo must be under ${GALLERY_MAX_FILE_BYTES / (1024 * 1024)}MB.`,
      );
    }

    const remaining = GALLERY_MAX_IMAGES - images.length - uploadingCount;
    const toUpload = accepted.slice(0, Math.max(0, remaining));
    if (toUpload.length < accepted.length) {
      toast.error(`A gallery can have at most ${GALLERY_MAX_IMAGES} photos.`);
    }
    if (!toUpload.length) return;

    const added = await uploads.upload(toUpload);
    if (added.length) onChange([...imagesRef.current, ...added]);
  };

  const openPicker = () => {
    if (disabled || isAtCap) return;
    inputRef.current?.click();
  };

  const confirmRemove = (imageRef: string) => {
    onChange(imagesRef.current.filter(image => image.imageRef !== imageRef));
    setPendingRemove(null);
  };

  const onRetry = async (localId: string) => {
    if (isAtCap) {
      toast.error(`A gallery can have at most ${GALLERY_MAX_IMAGES} photos.`);
      return;
    }
    const added = await uploads.retry(localId);
    if (added.length) onChange([...imagesRef.current, ...added]);
  };

  const countParts = [`${total} of ${GALLERY_MAX_IMAGES} photos`];
  if (uploadingCount) countParts.push(`${uploadingCount} uploading`);
  countParts.push('drag to reorder, first photo leads');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {helpText}
      </Typography>

      {isEmpty ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 4,
            px: 2,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
          }}>
          <CollectionsOutlinedIcon
            sx={{ fontSize: 36, color: 'text.tertiary', mb: 1 }}
          />
          <Typography variant="body1" sx={{ mb: 0.5 }}>
            No photos yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {emptyDetail}
          </Typography>
          <Box
            component="button"
            type="button"
            disabled={disabled}
            onClick={openPicker}
            sx={{
              ...resetButton,
              padding: '10px 18px',
              borderRadius: '50px',
              bgcolor: 'common.black',
              color: 'common.white',
              typography: 'body2',
              fontWeight: 700,
              '&:disabled': { opacity: 0.6 },
            }}>
            Add photos
          </Box>
        </Box>
      ) : (
        <SortableGrid
          items={images.map(image => ({ ...image, id: image.imageRef }))}
          minItemWidth={tileMinWidth}
          onChange={items =>
            onChange(items.map(({ id: _id, ...image }) => image))
          }
          trailing={
            <>
              {uploads.rows.map(row => (
                <UploadTile
                  key={row.localId}
                  row={row}
                  onRetry={() => onRetry(row.localId)}
                  onDismiss={() => uploads.dismiss(row.localId)}
                />
              ))}
              {!isAtCap && (
                <Box
                  component="button"
                  type="button"
                  disabled={disabled}
                  onClick={openPicker}
                  aria-label="Add photos"
                  sx={{
                    ...resetButton,
                    aspectRatio: '1',
                    borderRadius: 1.5,
                    border: '1px dashed',
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    color: 'text.secondary',
                    '&:disabled': { opacity: 0.6 },
                  }}>
                  <AddPhotoAlternateOutlinedIcon />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Add photos
                  </Typography>
                </Box>
              )}
            </>
          }
          renderItem={item => {
            const index = images.findIndex(
              image => image.imageRef === item.imageRef,
            );
            return (
              <Box
                sx={{
                  position: 'relative',
                  aspectRatio: '1',
                  borderRadius: 1.5,
                  overflow: 'hidden',
                  bgcolor: 'action.hover',
                }}>
                <Box
                  component="img"
                  src={galleryThumbnailUrl(item.imageRef)}
                  alt=""
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
                <SortableGrid.DragHandle
                  corner="left"
                  sx={{ ...tileChromeSx, left: '6px' }}
                  iconSx={{ width: 16, height: 16 }}
                />
                <IconButton
                  size="small"
                  disabled={disabled}
                  onClick={() => setPendingRemove(item.imageRef)}
                  aria-label="Remove photo"
                  sx={{ ...tileChromeSx, right: '6px', zIndex: 2 }}>
                  <CloseIcon sx={{ width: 15, height: 15 }} />
                </IconButton>
                <TileBadge index={index} leadCount={leadCount} />
                {pendingRemove === item.imageRef && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      zIndex: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      padding: '6px',
                      bgcolor: 'rgba(0,0,0,0.72)',
                    }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme => theme.palette.common.white,
                        fontWeight: 700,
                        lineHeight: 1.3,
                        textAlign: 'center',
                      }}>
                      Remove this photo?
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: '4px',
                        width: '100%',
                      }}>
                      <OverlayButton onClick={() => setPendingRemove(null)}>
                        Keep
                      </OverlayButton>
                      <OverlayButton
                        danger
                        onClick={() => confirmRemove(item.imageRef)}>
                        Remove
                      </OverlayButton>
                    </Box>
                  </Box>
                )}
              </Box>
            );
          }}
        />
      )}

      {(!isEmpty || dirty) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px',
          }}>
          <Typography variant="body2" color="text.secondary">
            {countParts.join(' · ')}
          </Typography>
          {dirty && (
            <Box
              sx={{
                flex: 'none',
                px: 1,
                py: 0.25,
                borderRadius: '50px',
                bgcolor: 'warning.main',
                color: 'warning.contrastText',
                typography: 'caption',
                fontWeight: 700,
              }}>
              Unsaved changes
            </Box>
          )}
        </Box>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*,.heic,.heif"
        multiple
        hidden
        onChange={event => onFilesPicked(event.target.files)}
      />
    </Box>
  );
}

export default GallerySection;
